import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Check } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Booking = () => {
  const [sessions, setSessions] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [sections, setSections] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, siteInfoRes, pageRes, sectionsRes] = await Promise.all([
        axios.get(`${API_URL}/sessions`),
        axios.get(`${API_URL}/site-info`),
        axios.get(`${API_URL}/page-content/booking`),
        axios.get(`${API_URL}/site-sections`)
      ]);
      setSessions(sessionsRes.data);
      setSiteInfo(siteInfoRes.data);
      setPageContent(pageRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load booking information');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!selectedSession || !selectedDate || !selectedTime || !customerName || !customerEmail) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/bookings`, {
        session_id: selectedSession.id,
        session_name: selectedSession.name,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        booking_date: selectedDate,
        booking_time: selectedTime,
        price: selectedSession.price
      }, {
        headers: { 'x-frontend-origin': window.location.origin }
      });

      // Redirect to Stripe checkout or show success
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Booking created successfully!');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to create booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const bookingControls = sections?.booking || {};
  const showSessionPrice = (session) => bookingControls.showPrices !== false && session.show_price !== false;
  const sessionPriceText = (session) => session.price_text || `$${session.price}`;

  if (bookingControls.enabled === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50 flex items-center justify-center px-4">
        <Card className="max-w-2xl border-0 shadow-xl bg-white">
          <CardContent className="p-10 text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-serif text-gray-900">
              {bookingControls.disabledTitle || 'Booking is currently unavailable'}
            </h1>
            <p className="text-lg text-gray-600">
              {bookingControls.disabledMessage || 'Please contact Lindsey directly for support or check back soon.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={siteInfo?.whatsapp || '#'} target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-sage-700 text-white rounded-xl hover:bg-sage-800 transition-all duration-300 hover:shadow-lg font-medium">
                Chat on WhatsApp
              </a>
              <a href={`tel:${siteInfo?.phone || ''}`} className="inline-block px-8 py-4 border-2 border-sage-700 text-sage-700 rounded-xl hover:bg-sage-50 transition-all duration-300 font-medium">
                Call Me
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{pageContent?.title || 'Book Your Session'}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent?.description || 'Choose a session type that fits your needs and schedule your healing journey'}
            </p>
          </div>
        </div>
      </section>

      {/* Session Selection */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-gray-900 text-center mb-12">{pageContent?.sessionTitle || 'Choose Your Session'}</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {sessions.map((session) => (
              <Card
                key={session.id}
                className={`cursor-pointer border-2 transition-all duration-300 hover:shadow-lg ${
                  selectedSession?.id === session.id
                    ? 'border-sage-600 shadow-lg bg-sage-50'
                    : 'border-sage-200 hover:border-sage-400 bg-white'
                }`}
                onClick={() => setSelectedSession(session)}
              >
                <CardContent className="p-8 space-y-4">
                  <div className="flex justify-between items-start">
                    <h3 className="text-2xl font-semibold text-gray-900">{session.name}</h3>
                    {selectedSession?.id === session.id && (
                      <div className="h-8 w-8 bg-sage-700 rounded-full flex items-center justify-center">
                        <Check className="h-5 w-5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-5 w-5 text-sage-600" />
                      <span>{session.duration}</span>
                    </div>
                    {showSessionPrice(session) && (
                      <div className="text-2xl font-bold text-sage-700">{sessionPriceText(session)}</div>
                    )}
                  </div>
                  <p className="text-gray-600 leading-relaxed">{session.description}</p>
                  {session.note && <p className="text-sm text-sage-700 font-medium">{session.note}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Date & Time Selection */}
      {selectedSession && (
        <section className="py-20 bg-sage-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-0 shadow-xl bg-white">
              <CardContent className="p-12 space-y-8">
                <h2 className="text-3xl font-serif text-gray-900 text-center">{pageContent?.formTitle || 'Select Date & Time'}</h2>
                
                <form onSubmit={handleBooking} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {pageContent?.nameLabel || 'Your Name *'}
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                      placeholder={pageContent?.namePlaceholder || 'Enter your name'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {pageContent?.emailLabel || 'Email Address *'}
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                      placeholder={pageContent?.emailPlaceholder || 'your.email@example.com'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {pageContent?.phoneLabel || 'Phone Number (Optional)'}
                    </label>
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                      placeholder={pageContent?.phonePlaceholder || '+1 (555) 000-0000'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-2" />
                      {pageContent?.dateLabel || 'Preferred Date'}
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="inline h-4 w-4 mr-2" />
                      {pageContent?.timeLabel || 'Preferred Time'}
                    </label>
                    <select
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                    >
                      <option value="">{pageContent?.timePlaceholder || 'Select a time'}</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="13:00">1:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                    </select>
                  </div>

                  <div className="bg-sage-50 p-6 rounded-xl space-y-3">
                    <h3 className="font-semibold text-gray-900">{pageContent?.summaryTitle || 'Booking Summary'}</h3>
                    <div className="space-y-2 text-gray-700">
                      <p><span className="font-medium">Session:</span> {selectedSession.name}</p>
                      <p><span className="font-medium">Duration:</span> {selectedSession.duration}</p>
                      {selectedDate && <p><span className="font-medium">Date:</span> {selectedDate}</p>}
                      {selectedTime && <p><span className="font-medium">Time:</span> {selectedTime}</p>}
                      <div className="pt-3 border-t border-sage-200">
                        {showSessionPrice(selectedSession) && (
                          <p className="text-2xl font-bold text-sage-700">Total: {sessionPriceText(selectedSession)}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="w-full bg-sage-700 hover:bg-sage-800 text-white py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-lg"
                  >
                    {submitting ? 'Processing...' : (bookingControls.buttonText || 'Proceed to Payment')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Contact Info */}
      {bookingControls.showHelp !== false && (
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl font-serif text-gray-900">{pageContent?.helpTitle || 'Need Help Choosing?'}</h2>
          <p className="text-lg text-gray-600">
            {pageContent?.helpText || "I'm happy to discuss which session would be best for your unique situation."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={siteInfo?.whatsapp || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-8 py-4 bg-sage-700 text-white rounded-xl hover:bg-sage-800 transition-all duration-300 hover:shadow-lg font-medium"
            >
              {pageContent?.whatsappLabel || 'Chat on WhatsApp'}
            </a>
            <a
              href={`tel:${siteInfo?.phone || ''}`}
              className="inline-block px-8 py-4 border-2 border-sage-700 text-sage-700 rounded-xl hover:bg-sage-50 transition-all duration-300 font-medium"
            >
              {pageContent?.callLabel || 'Call Me'}
            </a>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default Booking;
